import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  any, bool, func, oneOfType, shape, string
} from 'prop-types';
import axios from 'axios';
import { useCollectionDetailDeprecated, useProductDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import {
  extend,
  useCollectionDetail,
  params,
  string as stringType,
  shape as shapeType,
  arrayOf
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';

import Helper from './Helper';
import './media-gallery.style.scss';
import { componentReady } from '../publisher';
import { MediaGalleryMobile } from './subcomponents/MediaGalleryMobile';
import { MediaGalleryDesktop } from './subcomponents/MediaGalleryDesktop';
import { MediaHighlight } from './subcomponents/MediaHighlight/MediaHighlight';
import { MediaGalleryLoader } from './subcomponents/MediaGalleryLoader/MediaGalleryLoader';
import { dataModel } from './dataModel';

export const MediaGallery = ({
  collectionId,
  doubleTap,
  hideProductImagesLabel,
  highlight,
  highRes,
  hover,
  itemId,
  marqueeRef,
  marqueeRowRef,
  media,
  onMediaClick,
  showFavorites,
  showLoader,
}) => {
  const mediaMapRef = useRef(null);
  const [highResImages, setHighResImages] = useState([]);
  const highResImageRef = useRef(itemId);
  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';
  const storeId = useStoreId();
  const [hoverId, setHoverId] = useState('');
  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (hoverItemId) => {
        setHoverId(hoverItemId?.output);
      });
    }
  }, []);

  let [productRequst, productResponse] = useProductDeprecated({
    lazy: true,
    itemId: hoverId,
    domains: ['identifiers', 'media'],
    storeId,
  });
  useEffect(() => {
    if (hoverId) {
      productRequst({ variables: { hoverId } });
    }
  }, [hoverId]);

  useEffect(() => {
    if (itemId) {
      componentReady({ itemId });
    }
  }, []);

  const { data, error, loading } = useProductDeprecated({ itemId, domains: ['identifiers', 'media'], skip: !itemId });
  // const { data, error, loading } = useDataModel('product', { variables: { itemId }, skip: !itemId });

  const collectionData = useCollectionDetailDeprecated({ collectionId, storeId, skip: !collectionId });
  // const collectionData = useDataModel('collection', { variables: { collectionId, storeId }, skip: !collectionId });
  const apiState = media ? null : Helper.getAPIState({
    data: collectionId ? collectionData.data : data,
    loading: collectionId ? collectionData.loading : loading,
    error: collectionId ? collectionData.error : error,
    fetchId: itemId || collectionId
  });

  const { product } = (data || {});
  const { collection } = (collectionData || {}).data || {};
  const { metadata } = collection || {};

  // for swatch Hover
  const mediaSourceHover = collectionId ? (collection || {}).media : ((productResponse.data || {}).product || {}).media;
  const mediaListHover = Helper.getInitialMediaList({ media: media || mediaSourceHover });
  const mainHover = Helper.getStructuredMediaList({ mediaList: mediaListHover, size: '600' });

  const mediaSource = collectionId ? (collection || {}).media : (product || {}).media;
  const mediaList = Helper.getInitialMediaList({ media: media || mediaSource });

  const thumbnailSize = isMobile ? '100' : '145';
  const thumbnails = Helper.getStructuredMediaList({ mediaList, size: thumbnailSize });
  const main = Helper.getStructuredMediaList({ mediaList, size: '600' });
  const zoom = Helper.getStructuredMediaList({ mediaList, size: '1000' });
  const displayedMedia = {
    thumbnails,
    main,
    zoom,
  };

  useEffect(() => {
    // TODO: Hook
    if (channel !== 'mobile' && highRes) {
      const checkValidityPromise = [];
      zoom.forEach((zoomedImage) => {
        const imageCheckPromise = Helper.getHighResImages(zoomedImage, axios);
        checkValidityPromise.push(imageCheckPromise);
      });
      Promise.all(checkValidityPromise).then((responses) => {
        const images = responses.filter((resp) => !!resp);
        if (images.length) {
          setHighResImages(images);
        }

      }).catch(() => {});
    }
  }, [itemId]);

  if (apiState === 'loading') {
    return showLoader && <MediaGalleryLoader isMobile={isMobile} />;
  }

  if (apiState === 'error' || !mediaList?.length) {
    return null;
  }

  if (highlight) {
    return <MediaHighlight itemId={itemId} />;
  }

  const handleMediaClick = ({ media: clickedMedia }) => {
    if (onMediaClick) {
      onMediaClick({ media: clickedMedia });
    }
  };

  mediaMapRef.current = displayedMedia;

  if (highResImages) {
    mediaMapRef.current = {
      ...mediaMapRef.current,
      highResolution: highResImages
    };
  }

  return (
    <div>
      {isMobile ? (
        <MediaGalleryMobile
          media={mediaMapRef.current}
          onMediaClick={handleMediaClick}
          product={product || metadata}
          showFavorites={showFavorites}
        />
      ) : (
        <MediaGalleryDesktop
          media={mediaMapRef.current}
          collection={collection}
          product={product}
          totalThumbnailsToShow={6}
          hideProductImagesLabel={hideProductImagesLabel}
          mainHover={mainHover}
          showFavorites={showFavorites}
          marqueeRef={marqueeRef}
          marqueeRowRef={marqueeRowRef}
        />
      )}
    </div>
  );
};

MediaGallery.displayName = 'MediaGallery';

MediaGallery.propTypes = {
  collectionId: string,
  doubleTap: bool,
  hideProductImagesLabel: bool,
  highlight: bool,
  highRes: bool,
  hover: bool,
  itemId: string,
  onMediaClick: func,
  marqueeRef: oneOfType([
    func,
    shape({})
  ]),
  marqueeRowRef: oneOfType([
    func,
    shape({})
  ]),
  media: shape({}),
  showLoader: bool,
  showFavorites: bool
};

MediaGallery.defaultProps = {
  collectionId: null,
  doubleTap: false,
  hideProductImagesLabel: false,
  highlight: false,
  highRes: false,
  hover: false,
  itemId: null,
  onMediaClick: null,
  marqueeRef: null,
  marqueeRowRef: null,
  media: null,
  showLoader: false,
  showFavorites: false
};

MediaGallery.dataModel = dataModel;
